import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Calendar,
  MapPin,
  Users,
  ExternalLink,
  ArrowRight,
  Star,
  Award,
} from "react-feather";
import PageShell from "../../components/premium/layout/PageShell";
import LaunchCompletedBadge from "../../components/premium/launch/LaunchCompletedBadge";
import {
  LAUNCH_EVENT,
  LAUNCH_HIGHLIGHTS,
  LAUNCH_MILESTONES,
  LAUNCH_GUESTS,
} from "../../data/launchEventConfig";
import rudraLogo from "../../assets/images/rudra-ride-logo.png";
import eduLogo from "../../assets/images/edu-pravahaa-logo.png";
import MagneticButton from "../../components/premium/hero/MagneticButton";

const logos = { "Rudra Ride": rudraLogo, "Edu Pravahaa": eduLogo };

export default function LaunchEventPage() {
  return (
    <PageShell>
      {/* Hero */}
      <section className="relative py-20 lg:py-28 text-center overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(34,197,94,0.12),transparent_55%)]" />
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <LaunchCompletedBadge className="mb-6" />
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tight">
              Grand Launch{" "}
              <span className="premium-heading-gradient">Recap</span>
            </h1>
            <p className="mt-6 text-lg sm:text-xl text-white/65 leading-relaxed max-w-2xl mx-auto">
              On {LAUNCH_EVENT.day}, {LAUNCH_EVENT.date}, EPT Pravahaa celebrated the successful
              launch of Rudra Ride and Edu Pravahaa at {LAUNCH_EVENT.venue}, Tirupathi.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Milestones */}
      <section className="pb-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="launch-recap-card overflow-hidden"
          >
            <div className="px-6 py-4 border-b border-white/8">
              <p className="text-[#F5C044] text-xs font-bold uppercase tracking-widest">
                Event timeline
              </p>
            </div>
            <div className="launch-milestone-track">
              {LAUNCH_MILESTONES.map((m) => (
                <div key={m.step} className="launch-milestone-item">
                  <p className="launch-milestone-step">{m.step}</p>
                  <p className="text-white font-bold mt-2">{m.label}</p>
                  <p className="text-white/50 text-sm mt-1">{m.detail}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Platforms launched */}
      <section className="py-12 lg:py-16 premium-section-alt">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="premium-badge mb-4">Platforms unveiled</span>
            <h2 className="text-3xl md:text-4xl font-bold text-white">What We Launched</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {LAUNCH_HIGHLIGHTS.map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="launch-recap-card p-8"
              >
                <div className="rounded-2xl bg-white p-6 mb-6 flex justify-center">
                  <img src={logos[item.title]} alt={item.title} className="max-h-24 object-contain" />
                </div>
                <div className="flex items-center gap-2 mb-3">
                  <Award size={18} style={{ color: item.accent }} />
                  <span
                    className="text-xs font-bold uppercase tracking-wider"
                    style={{ color: item.accent }}
                  >
                    Officially launched
                  </span>
                </div>
                <h3 className="text-2xl font-bold text-white">{item.title}</h3>
                <p className="mt-3 text-white/60 leading-relaxed">{item.desc}</p>
                <Link
                  to={item.link}
                  className="mt-6 inline-flex items-center gap-2 font-semibold"
                  style={{ color: item.accent }}
                >
                  Visit platform <ArrowRight size={18} />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Event details */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-white text-center mb-10">Event details</h2>
          <div className="grid sm:grid-cols-3 gap-6">
            {[
              {
                icon: Calendar,
                title: "Date",
                value: LAUNCH_EVENT.dateShort,
                sub: `${LAUNCH_EVENT.day}, ${LAUNCH_EVENT.time}`,
              },
              {
                icon: MapPin,
                title: "Venue",
                value: LAUNCH_EVENT.venue,
                sub: "Tirupathi",
              },
              {
                icon: MapPin,
                title: "Location",
                value: "Ramanuja Circle",
                sub: LAUNCH_EVENT.address,
              },
            ].map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="launch-detail-card text-center sm:text-left"
              >
                <item.icon className="text-[#F5C044] mx-auto sm:mx-0 mb-4" size={26} />
                <p className="text-white/45 text-xs uppercase tracking-wider">{item.title}</p>
                <p className="text-white text-xl font-bold mt-1">{item.value}</p>
                <p className="text-white/55 text-sm mt-2">{item.sub}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Guests */}
      <section className="py-12 premium-section-alt">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h3 className="text-xl font-bold text-white mb-2 flex items-center justify-center gap-2">
            <Users size={22} className="text-[#F5C044]" />
            Honored guests & partners
          </h3>
          <p className="text-white/50 text-sm mb-8">
            Thank you to everyone who joined us at the grand launch.
          </p>
          <div className="flex flex-wrap justify-center gap-2">
            {LAUNCH_GUESTS.map((g) => (
              <span key={g} className="launch-guest-chip">
                {g}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Thank you / CTA */}
      <section className="py-16 lg:py-20 text-center">
        <Star className="mx-auto text-[#F5C044] mb-4" size={28} />
        <h3 className="text-2xl font-bold text-white mb-3">The journey continues</h3>
        <p className="text-white/55 max-w-lg mx-auto mb-10">
          Explore our live platforms and enroll in programs designed for your future.
        </p>
        <div className="flex flex-wrap justify-center gap-4">
          <MagneticButton to="/enroll" variant="gold">
            Enroll Now
          </MagneticButton>
          <MagneticButton to="/products" variant="primary">
            View products
          </MagneticButton>
          <MagneticButton href={LAUNCH_EVENT.mapUrl} variant="glass">
            <ExternalLink size={18} /> Venue on map
          </MagneticButton>
        </div>
      </section>
    </PageShell>
  );
}
